import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Leaf, ArrowRight, AlertTriangle, Zap,
  Plus, X, Check, MapPin, ScanLine, CheckCircle2, Camera, RotateCcw, ChevronRight,
  Package, ChevronDown, Loader2, Info,
} from "lucide-react";
import { estimatePartFootprint, FOOTPRINT_METHOD } from "@/lib/partFootprint";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import {
  buildSparePartPrintJobs,
  resolveSparePartLabelSize,
  useLabelPrinter as useHarvestLabelPrinter,
  useLabelPrintingSettings as useHarvestLabelPrintingSettings,
} from "@/labels";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { sparePartDonorRow, type DonorAppliance } from "@/lib/sparePartDonor";
import { useWorkOrderProgress } from "@/hooks/useWorkOrderProgress";
import { MatchingManualsPanel } from "@/components/MatchingManualsPanel";
import { ExplodedViewsPanel } from "@/components/workflow/ExplodedViewsPanel";
import { seriesLabelFor } from "@/hooks/useMatchingManuals";
import { AIAssistantDialog } from "@/components/AIAssistantDialog";
import { useHarvestComponentsForBrand } from "@/hooks/useHarvestComponents";
import { useHarvestRecommendations } from "@/hooks/useHarvestRecommendations";
import { useApplianceDryingTechnology } from "@/components/DryingTechnologyField";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { dryingTechnologyKind } from "@/lib/dryerTechnology";
import { groupByTechnology, totalRecoveryRange, type PartTechnology } from "@/lib/harvestTechnologyGroups";
import { normalizeResalePct, resaleValue } from "@/lib/resalePct";
import { stablePartId, dedupeById, effectiveSelection, deselectedFromSelection, migrateLegacySelection, mergeUncertainByName, isEnginePartId, stepPersistPatch, uniqueByName } from "@/lib/harvestSelection";
import { getHarvestApplianceCategoryLabel } from "@/lib/applianceCategoryLabel";
import { useSparePartsByIds } from "@/hooks/useSpareParts";
import { useAllStorageBins } from "@/hooks/useStorageBins";
import { QRLabel } from "@/components/QRLabel";
import { SparePartLabel, formatDate as formatLabelDate } from "@/components/SparePartLabel";
import { BinScanner } from "@/components/BinScanner";
import { BinAssignModal } from "@/components/harvest/BinAssignModal";
import { BinLocationChoiceDialog, type BinLocationChoice } from "@/components/BinLocationChoiceDialog";
import { resolveScannedBin } from "@/lib/binScan";
import { SelectionActionBar } from "@/components/harvest/SelectionActionBar";
import { BinHelpModal } from "@/components/harvest/BinHelpModal";
import { useScannerInput } from "@/hooks/useScannerInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CompleteApplianceDialog } from "@/components/harvest/CompleteApplianceDialog";
import { hasCategory, missingApplianceFields, needsBrandReview, recommendationRequestFor } from "@/lib/applianceDonorCompletion";
import { useAppliance } from "@/hooks/useAppliance";
import { useWorkflowRunner } from "@/workflows/runnerContext";
import { HarvestWorkflowHeader } from "@/components/harvest/HarvestWorkflowHeader";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useSidebar } from "@/components/ui/sidebar";
import { resolvePartTypeId } from "@/lib/resolvePartType";
import { addPrinted, allLabelsPrinted, hydratePrinted, idsNeedingConfirmation, printAfterMenuCloses } from "@/lib/harvestLabelPrinting";
import { HarvestLabelPrintRoot, HarvestPrintLabelPage } from "@/components/harvest/HarvestLabelPrintRoot";


interface HarvestComponent {
  id: string;
  name: string;
  description: string;
  category: string;
  material: string;
  weight: number;
  value: number;
  recovery: number;
  co2eKg: number;
  isHazardous?: boolean;
  technology?: PartTechnology;
}



export default function HarvestProcess() {
  const { t, i18n } = useTranslation("harvest");
  const navigate = useNavigate();
  const runner = useWorkflowRunner();
  const { id } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { state: sidebarState, isMobile: isSidebarMobile } = useSidebar();
  const ctaLeftOffset = isSidebarMobile
    ? "0px"
    : sidebarState === "collapsed"
      ? "var(--sidebar-width-icon)"
      : "var(--sidebar-width)";


  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [sparePartIds, setSparePartIds] = useState<string[]>([]);
  // Per-part printed status; null = nothing saved yet (legacy flag may apply).
  const [printedPartIds, setPrintedPartIds] = useState<string[] | null>(null);
  const [legacyLabelsPrinted, setLegacyLabelsPrinted] = useState(false);
  const [confirmPrintIds, setConfirmPrintIds] = useState<string[] | null>(null);
  const [confirmPrintVia, setConfirmPrintVia] = useState<"browser" | "zebra">("browser");
  const [lastScannedBinId, setLastScannedBinId] = useState<string | null>(null);

  // FIX-273: what the technician UNticked (stable ids) is the source of truth.
  const [deselectedIds, setDeselectedIds] = useState<string[]>([]);
  
  const [customComponents, setCustomComponents] = useState<HarvestComponent[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemWeight, setNewItemWeight] = useState("");
  const [newItemValue, setNewItemValue] = useState("");
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [manualId, setManualId] = useState<string | null>(null);
  const [manualTitle, setManualTitle] = useState<string | null>(null);
  const [manualIsFallback, setManualIsFallback] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [scanningPartId, setScanningPartId] = useState<string | null>(null);
  const [scannerStream, setScannerStream] = useState<MediaStream | null>(null);
  const [showAddPartDialog, setShowAddPartDialog] = useState(false);
  const [addPartComponentId, setAddPartComponentId] = useState<string>("");
  const [printOnlyId, setPrintOnlyId] = useState<string | null>(null);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState<string | null>(null);
  const [advancingToLabels, setAdvancingToLabels] = useState(false);
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(new Set());
  const [binModalOpen, setBinModalOpen] = useState(false);
  // An old bin label whose code exists in two locations: ask which one (FIX-319).
  const [binChoice, setBinChoice] = useState<BinLocationChoice | null>(null);
  const [showBinHelp, setShowBinHelp] = useState(false);

  const effectiveApplianceId = runner?.applianceId || id;
  const { appliance: loadedAppliance, notFound, reload: reloadAppliance } = useAppliance(effectiveApplianceId);
  const currentCompany = useCurrentCompany();

  useEffect(() => {
    if (notFound) {
      toast.error(t("process.applianceNotFound"));
      navigate("/work-orders");
    }
  }, [notFound, navigate, t]);

  // Keep the same shape that the rest of this file expects
  const storedAppliance = loadedAppliance
    ? {
        id: loadedAppliance.id,
        name: loadedAppliance.name,
        brand: loadedAppliance.brand,
        modelNumber: loadedAppliance.modelNumber,
        category: (loadedAppliance.category || "other").toLowerCase().replace(/\s+/g, "-"),
      }
    : null;
  const applianceBrand = loadedAppliance?.brand || loadedAppliance?.name?.split(" ")[0];
  const applianceCategoryLabel = getHarvestApplianceCategoryLabel(storedAppliance?.category);
  const { data: dbComponents = [] } = useHarvestComponentsForBrand(
    applianceBrand,
    applianceCategoryLabel,
    applianceCategoryLabel,
    effectiveApplianceId,
  );
  const isDbBackedAppliance = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(effectiveApplianceId || "");
  // FIX-286: the donor appliance row drives the "Complete appliance details" check.
  const { data: donorAppliance, isLoading: donorLoading } = useQuery<DonorAppliance | null>({
    queryKey: ["donor_appliance", effectiveApplianceId],
    enabled: isDbBackedAppliance,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appliances").select("id, category, brand, model_number, serial_number").eq("id", effectiveApplianceId!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const categoryMissing = isDbBackedAppliance && !donorLoading && !!donorAppliance && !hasCategory(donorAppliance);
  const [completeOpen, setCompleteOpen] = useState(false);
  const autoPromptedFor = useRef<string | null>(null);
  useEffect(() => {
    if (step !== 1 || !donorAppliance || autoPromptedFor.current === donorAppliance.id) return;
    if (missingApplianceFields(donorAppliance).length > 0) {
      autoPromptedFor.current = donorAppliance.id;
      setCompleteOpen(true);
    }
  }, [step, donorAppliance]);
  const onApplianceCompleted = (updated: DonorAppliance) => {
    setCompleteOpen(false);
    if (brandReview) confirmBrand((updated.brand ?? "").trim());
    queryClient.setQueryData(["donor_appliance", effectiveApplianceId], updated);
    queryClient.invalidateQueries({ queryKey: ["appliance", effectiveApplianceId] });
    reloadAppliance();
    queryClient.invalidateQueries({ queryKey: ["harvest_recommendations"] });
    queryClient.invalidateQueries({ queryKey: ["harvest_components_for_brand"] });
  };

  const recommendationRequest = recommendationRequestFor(
    isDbBackedAppliance,
    donorAppliance,
    isDbBackedAppliance
      ? effectiveApplianceId
      : loadedAppliance && currentCompany.id
        ? {
            companyId: currentCompany.id,
            category: loadedAppliance.category,
            technology: loadedAppliance.dryerTechnology || "",
            brand: loadedAppliance.brand || loadedAppliance.name?.split(" ")[0] || "",
          }
        : null,
  );
  const { data: recommendations, isLoading: recsLoading, error: recsError } =
    useHarvestRecommendations(recommendationRequest);
  // FIX-286: unrecognised brand → review once per work order (or appliance without one).
  const brandConfirmKey = `fixid:brandConfirmed:${runner?.workOrderId || effectiveApplianceId}`;
  const [localBrandConfirmed, setLocalBrandConfirmed] = useState<string | null>(() => {
    try { return localStorage.getItem(brandConfirmKey); } catch { return null; }
  });
  const brandConfirmedFor = (runner?.state as { brandConfirmedFor?: string } | undefined)?.brandConfirmedFor ?? localBrandConfirmed;
  const brandReview = !!donorAppliance && needsBrandReview(
    recommendations?.matchedOn?.brandRecognised,
    donorAppliance.brand,
    brandConfirmedFor,
  );
  const confirmBrand = (brand: string) => {
    setLocalBrandConfirmed(brand);
    try { localStorage.setItem(brandConfirmKey, brand); } catch { /* ignore */ }
    if (runner) void runner.persist({ brandConfirmedFor: brand } as never);
  };
  const brandPromptedFor = useRef<string | null>(null);
  useEffect(() => {
    if (step !== 1 || !brandReview || !donorAppliance) return;
    const k = `${donorAppliance.id}|${donorAppliance.brand}`;
    if (brandPromptedFor.current === k) return;
    brandPromptedFor.current = k;
    setCompleteOpen(true);
  }, [step, brandReview, donorAppliance]);
  const { data: bins = [] } = useAllStorageBins();
  const { data: spareParts = [], refetch: refetchParts } = useSparePartsByIds(sparePartIds);

  const recsExpected = Boolean(recommendationRequest);
  const harvestableComponents: HarvestComponent[] = useMemo(() => {
    // Engine recommendations are the primary source. Fall back to legacy DB
    // components only if no active sheet is configured.
    if (recommendations && recommendations.items.length > 0) {
      const recMapped: HarvestComponent[] = dedupeById(recommendations.items.map((r) => {
        const fp = estimatePartFootprint({ name: r.partName, partType: r.partCategory });
        return {
          id: stablePartId(r.partName, r.partCategory, r.technology),
          name: r.partName,
          description: "",
          category: "metal",
          material: r.partCategory || "Unknown",
          weight: fp.weightKg,
          value: r.brandPrice ?? 0,
          recovery: 100,
          isHazardous: false,
          co2eKg: fp.totalKg,
          technology: r.technology ?? "all",
        };
      }));
      return [...recMapped, ...customComponents];
    }
    // FIX-257: engine answered with 0 parts → empty list, no legacy fallback.
    const engineAnsweredEmpty = !!recommendations && recommendations.items.length === 0;
    if (engineAnsweredEmpty) return [...customComponents];
    // While the engine request is in-flight, avoid flashing the legacy list.
    // FIX-286: no parts at all until the appliance has a category.
    if ((recsExpected && recsLoading) || (isDbBackedAppliance && (donorLoading || categoryMissing))) {
      return [...customComponents];
    }
    const dbMapped: HarvestComponent[] = dbComponents.map((c) => {
      const cat = (c.category || "").toLowerCase();
      const normCat = ["metal", "plastic", "rubber", "electronic", "rare-earth"].includes(cat)
        ? cat
        : cat === "electronics" ? "electronic"
        : cat === "glass" ? "plastic"
        : cat === "component" ? "electronic"
        : "metal";
      const dbWeight = Number(c.default_weight_kg) || 0;
      const fp = estimatePartFootprint({ name: c.name, partType: c.name, weightKg: dbWeight });
      return {
        id: c.id,
        name: c.name,
        description: c.description || "",
        category: normCat,
        material: c.material || "Unknown",
        weight: dbWeight > 0 ? dbWeight : fp.weightKg,
        value: Number(c.default_value) || 0,
        recovery: Number(c.default_recovery_pct) || 100,
        isHazardous: c.is_hazardous,
        co2eKg: fp.totalKg,
      };
    });
    return [...dbMapped, ...customComponents];
  }, [recommendations, dbComponents, customComponents, recsExpected, recsLoading, isDbBackedAppliance, donorLoading, categoryMissing]);

  // FIX-288: technology-dependent parts when the drying technology is unknown.
  const { data: dryingInfo } = useApplianceDryingTechnology(isDbBackedAppliance ? effectiveApplianceId : null);
  const techGrouping = useMemo(
    () =>
      groupByTechnology(
        harvestableComponents,
        dryingInfo?.category ?? loadedAppliance?.category,
        isDbBackedAppliance ? dryingInfo?.dryer_technology : loadedAppliance?.dryerTechnology,
        dryingInfo?.appliance_class,
      ),
    [harvestableComponents, dryingInfo, loadedAppliance?.category, loadedAppliance?.dryerTechnology, isDbBackedAppliance],
  );
  // FIX-312: "Add harvested spare part" list shows each part name once.
  const addPartItems = useMemo(
    () =>
      uniqueByName(
        harvestableComponents,
        isDbBackedAppliance ? dryingInfo?.dryer_technology : loadedAppliance?.dryerTechnology,
      ).map((c) => ({ value: c.id, label: c.name })),
    [harvestableComponents, dryingInfo?.dryer_technology, loadedAppliance?.dryerTechnology, isDbBackedAppliance],
  );
  const uncertainIds = useMemo(() => new Set(techGrouping.uncertain.map((c) => c.id)), [techGrouping]);
  // Ticking an uncertain unit asks to confirm its technology first.
  const allComponentIds = useMemo(() => harvestableComponents.map((c) => c.id), [harvestableComponents]);
  const selectedComponents = useMemo(
    () => effectiveSelection(allComponentIds, deselectedIds, uncertainIds),
    [allComponentIds, deselectedIds, uncertainIds],
  );
  // Ticking an uncertain row asks which technology (one or several choices).
  const [pendingTechs, setPendingTechs] = useState<PartTechnology[] | null>(null);
  const [savingTech, setSavingTech] = useState(false);
  const dryingKind = dryingTechnologyKind(dryingInfo?.category ?? loadedAppliance?.category, dryingInfo?.appliance_class);


  // Persistence: when running inside WorkflowRunner, route saves through the
  // runner so we don't clobber `data.modules` / `data.currentStep` written by it.
  // When standalone (legacy direct route), fall back to the old per-work-order writer.
  const { savedProgress, saveProgress } = useWorkOrderProgress(
    storedAppliance?.id || effectiveApplianceId,
    "harvest"
  );

  type HarvestModuleState = {
    harvestStep?: 1 | 2 | 3 | 4 | 5;
    sparePartIds?: string[];
    /** Derived output for later steps; not read back once deselectedComponentIds exists. */
    selectedComponentIds?: string[];
    /** FIX-273: stable ids the technician unticked. */
    deselectedComponentIds?: string[];
    customComponents?: HarvestComponent[];
    labelsPrinted?: boolean;
    /** Parts whose label was confirmed printed (browser) or sent to Zebra successfully. */
    printedPartIds?: string[];
    /** FIX-286: brand the technician confirmed although the rules sheet doesn't know it. */
    brandConfirmedFor?: string;
  };

  const runnerState = runner?.state as HarvestModuleState | undefined;

  /** Applies a wanted selection: stores the deselected ids and persists both. */
  const applySelection = (next: string[], nextCustom?: HarvestComponent[]) => {
    const ids = nextCustom
      ? [...allComponentIds.filter((id) => !id.startsWith("custom-")), ...nextCustom.map((c) => c.id)]
      : allComponentIds;
    const d = deselectedFromSelection(ids, next, deselectedIds, uncertainIds);
    setDeselectedIds(d);
    if (nextCustom) setCustomComponents(nextCustom);
    if (runner) {
      runner.persist({
        harvestStep: 1,
        sparePartIds,
        selectedComponentIds: effectiveSelection(ids, d, uncertainIds),
        deselectedComponentIds: d,
        customComponents: nextCustom ?? customComponents,
      });
    }
  };

  // Hydrate sub-step on mount. Prefer runner state (canonical when in runner),
  // fall back to legacy savedProgress for the standalone direct route.
  useEffect(() => {
    if (runner) {
      if (runnerState?.deselectedComponentIds) {
        setDeselectedIds(runnerState.deselectedComponentIds);
      }
      if (runnerState?.customComponents) {
        setCustomComponents(runnerState.customComponents);
      }
      if (runnerState?.printedPartIds) {
        setPrintedPartIds(runnerState.printedPartIds);
      } else if (runnerState?.labelsPrinted) {
        setLegacyLabelsPrinted(true);
      }
      const hs = runnerState?.harvestStep;
      if (hs && hs >= 2 && runnerState?.sparePartIds?.length) {
        setSparePartIds(runnerState.sparePartIds);
        setStep(hs);
      }
      return;
    }
    const hs = savedProgress?.harvestStep;
    if (hs && hs >= 2 && savedProgress?.sparePartIds?.length) {
      setSparePartIds(savedProgress.sparePartIds);
      setStep(hs);
    }
    if (savedProgress?.printedPartIds) {
      setPrintedPartIds(savedProgress.printedPartIds);
    } else if (savedProgress?.labelsPrinted) {
      setLegacyLabelsPrinted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProgress, runnerState?.harvestStep, (runnerState?.sparePartIds || []).join(",")]);


  // FIX-273: everything is ticked unless deselected. Old work orders that only
  // stored position-based ticked ids are converted once to deselected ids.
  const migratedLegacy = useRef(false);
  useEffect(() => {
    if (migratedLegacy.current || !runner) return;
    if (harvestableComponents.length === 0 || (recsExpected && recsLoading)) return;
    migratedLegacy.current = true;
    if (runnerState?.deselectedComponentIds) return;
    const old = runnerState?.selectedComponentIds;
    const d = old && old.length > 0 ? migrateLegacySelection(old, harvestableComponents, uncertainIds) : [];
    setDeselectedIds(d);
    if ((runnerState?.harvestStep ?? 1) > 1) return;
    runner.persist({
      harvestStep: 1,
      sparePartIds,
      selectedComponentIds: effectiveSelection(allComponentIds, d, uncertainIds),
      deselectedComponentIds: d,
      customComponents,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [harvestableComponents, recsExpected, recsLoading]);

  // Once a technology is chosen and the engine has re-filtered for it,
  // select the remaining technology-specific unit(s).
  const wasUnclear = useRef(false);
  useEffect(() => {
    if (techGrouping.unclear) {
      wasUnclear.current = true;
      return;
    }
    if (!wasUnclear.current || recsLoading || !recommendations?.matchedOn?.technology) return;
    wasUnclear.current = false;
    const techIds = harvestableComponents
      .filter((c) => c.technology && c.technology !== "all")
      .map((c) => c.id);
    if (techIds.length === 0) return;
    applySelection([...new Set([...selectedComponents, ...techIds])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techGrouping.unclear, harvestableComponents, recsLoading, recommendations?.matchedOn?.technology]);

  const confirmTechnology = async (pendingTech: PartTechnology) => {
    if (!effectiveApplianceId) return;
    setSavingTech(true);
    const { error } = await supabase.from("appliances").update({ dryer_technology: pendingTech }).eq("id", effectiveApplianceId);
    setSavingTech(false);
    if (error) {
      toast.error(t("process.confirmTechnology.saveFailed"));
      return;
    }
    queryClient.setQueryData(["appliance_drying_technology", effectiveApplianceId], (old: any) => (old ? { ...old, dryer_technology: pendingTech } : old));
    queryClient.invalidateQueries({ queryKey: ["harvest_recommendations"] });
    setPendingTechs(null);
  };

  // Unified saver: runner-aware in workflow runner, legacy writer otherwise.
  const persistSubStep = async (rawPatch: HarvestModuleState & { harvestStep: 1 | 2 | 3 | 4 | 5; sparePartIds: string[]; allPlaced?: boolean; freezeStepOne?: boolean }) => {
    // FIX-273: the step-1 record is written only by step 1 and the
    // "Continue to extraction" save; later steps never overwrite it.
    const patch = stepPersistPatch(rawPatch);
    if (runner) {
      await runner.persist(patch);
      return;
    }
    const titles: Record<number, string> = {
      1: "Select Materials",
      2: "Extract & Confirm",
      3: "Print Labels",
      4: "Store in Bins",
      5: "Work Order Complete",
    };
    const phaseNames: Record<number, string> = {
      1: "select", 2: "extract", 3: "label", 4: "store", 5: "complete",
    };
    const totalSteps = 5;
    const completedCount = patch.harvestStep === 5 ? 5 : patch.harvestStep - 1;
    const completedSteps = Array.from({ length: completedCount }, (_, i) => i);
    saveProgress({
      currentStepIndex: patch.harvestStep - 1,
      completedSteps,
      currentPhaseName: phaseNames[patch.harvestStep],
      currentPhaseCategory: "harvest",
      currentStepTitle: titles[patch.harvestStep],
      totalSteps,
      completedCount,
      harvestStep: patch.harvestStep,
      sparePartIds: patch.sparePartIds,
      labelsPrinted: patch.labelsPrinted,
      printedPartIds: patch.printedPartIds ?? printedPartIds ?? undefined,
    });
  };


  const addCustomItem = () => {
    if (!newItemName.trim()) return;
    const customWeight = parseFloat(newItemWeight) || 0;
    const newItem: HarvestComponent = {
      id: `custom-${Date.now()}`,
      name: newItemName.trim(),
      description: t("process.custom"),
      category: "",
      material: "",
      weight: customWeight,
      value: parseFloat(newItemValue) || 0,
      recovery: 80,
      co2eKg: estimatePartFootprint({ name: newItemName.trim(), weightKg: customWeight }).totalKg,
    };
    const nextCustomComponents = [...customComponents, newItem];
    applySelection([...selectedComponents, newItem.id], nextCustomComponents);
    setNewItemName(""); setNewItemWeight(""); setNewItemValue("");
    setShowAddForm(false);
  };

  const removeCustomItem = (itemId: string) => {
    const nextCustomComponents = customComponents.filter((c) => c.id !== itemId);
    applySelection(selectedComponents.filter((sid) => sid !== itemId), nextCustomComponents);
  };

  const toggleComponent = (componentId: string) => {
    if (uncertainIds.has(componentId)) {
      const tech = techGrouping.uncertain.find((c) => c.id === componentId)?.technology;
      if (tech && tech !== "all") setPendingTechs([tech]);
      return;
    }
    applySelection(
      selectedComponents.includes(componentId)
        ? selectedComponents.filter((sid) => sid !== componentId)
        : [...selectedComponents, componentId],
    );
  };

  // FIX-257: 0 means 0%; the fallback applies only when the value is missing.
  const resalePctFraction = useMemo(
    () => normalizeResalePct(recommendations?.thresholds?.resale_price_pct),
    [recommendations?.thresholds?.resale_price_pct],
  );

  const selectionSummary = useMemo(() => {
    const selected = harvestableComponents.filter((c) => selectedComponents.includes(c.id));
    const totalWeight = selected.reduce((s, c) => s + c.weight, 0);
    const totalValue = selected.reduce((s, c) => s + c.value, 0);
    const [recoveryMin, recoveryMax] = totalRecoveryRange(techGrouping, resalePctFraction);
    const totalRecoveryValue = recoveryMax;
    const recoveredValue = resaleValue(totalValue, resalePctFraction);
    const avgRecovery = totalRecoveryValue > 0
      ? (recoveredValue / totalRecoveryValue) * 100
      : 0;

    return {
      count: selected.length,
      totalWeight: totalWeight.toFixed(1),
      totalValue: totalValue.toFixed(2),
      totalRecoveryValue: totalRecoveryValue.toFixed(2),
      totalRecoveryLabel: techGrouping.unclear
        ? `€${recoveryMin.toFixed(0)}–€${recoveryMax.toFixed(0)}`
        : `€${totalRecoveryValue.toFixed(2)}`,
      avgRecovery: avgRecovery.toFixed(0),
      co2: selected.reduce((s, c) => s + c.co2eKg, 0).toFixed(1),
    };
  }, [selectedComponents, harvestableComponents, resalePctFraction, techGrouping]);


  // FIX-286: every new part needs its donor appliance, work order and category.
  const loadDonorAppliance = async (): Promise<DonorAppliance | null> => {
    const aid = effectiveApplianceId;
    if (!aid || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(aid)) return null;
    const { data, error } = await supabase
      .from("appliances").select("id, category, brand, model_number, serial_number").eq("id", aid).maybeSingle();
    if (error) throw error;
    if (data) queryClient.setQueryData(["donor_appliance", aid], data);
    return data;
  };
  const reportDonorMissing = (missing: string[]) => {
    const fields = missing.map((f) => t(`process.donorField.${f}`)).join(", ");
    toast.error(t("process.donorMissing", { fields }), {
      duration: 10000,
      action: effectiveApplianceId
        ? { label: t("process.openAppliance"), onClick: () => setCompleteOpen(true) }
        : undefined,
    });
  };

  const continueToLabels = async () => {
    if (!user) { toast.error(t("process.loginRequired")); return; }
    const selected = harvestableComponents.filter((c) => selectedComponents.includes(c.id));
    if (selected.length === 0) return;
    setProcessing(true);
    try {
      const { data: companyId, error: cErr } = await supabase
        .rpc("get_user_company_id", { _user_id: user.id });
      if (cErr) throw cErr;
      if (!companyId) throw new Error(t("process.noCompany"));

      const donorAppliance = await loadDonorAppliance();
      const donorRows = selected.map((c) =>
        sparePartDonorRow(donorAppliance, runner?.workOrderId, { id: c.id, name: c.name, material: c.material }));
      const missing = [...new Set(donorRows.flatMap((r) => ("missing" in r ? r.missing : [])))];
      if (missing.length) { reportDonorMissing(missing); return; }

      const rows = await Promise.all(
        selected.map(async (c, i) => ({
          ...(donorRows[i] as Extract<(typeof donorRows)[number], { ok: true }>).row,
          company_id: companyId,
          origin_company_id: companyId, // overwritten by trigger
          user_id: user.id,
          component_id: (c.id.startsWith("custom-") || isEnginePartId(c.id)) ? null : c.id,
          part_type_id: await resolvePartTypeId(
            c.name,
            applianceBrand || null,
            isEnginePartId(c.id) ? c.material : null,
          ),
          name: c.name,
          weight_kg: c.weight,
          co2e_avoided_kg: c.co2eKg,
          co2e_method: FOOTPRINT_METHOD,
          estimated_value: c.value,
          notes: c.isHazardous ? "Hazardous material" : null,
        })),
      );

      const { data: inserted, error } = await supabase
        .from("spare_parts").insert(rows).select("id");
      if (error && /appliance_company_mismatch|work_order_mismatch/.test(error.message)) { toast.error(t("donor.otherCompany")); return; }
      if (error) throw error;

      const newIds = (inserted || []).map((r: { id: string }) => r.id);
      setSparePartIds(newIds);
      setStep(2);
      await persistSubStep({
        harvestStep: 2,
        sparePartIds: newIds,
        selectedComponentIds: selectedComponents,
        deselectedComponentIds: deselectedIds,
        customComponents,
        freezeStepOne: true,
      });
      toast.success(t("process.partsCreated", { count: selected.length }));
    } catch (e) {
      const err = e as Error;
      toast.error(err.message || t("process.failedToProcess"));
    } finally {
      setProcessing(false);
    }
  };

  const assignBin = async (partId: string, binId: string) => {
    setAssigning(partId);
    const { error } = await supabase
      .from("spare_parts")
      .update({ bin_id: binId, status: "stored" })
      .eq("id", partId);
    setAssigning(null);
    if (error) {
      toast.error(t("process.failedToAssign", { error: error.message }));
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", sparePartIds.join(",")] });
    queryClient.invalidateQueries({ queryKey: ["spare_parts"] });
    refetchParts();
    toast.success(t("process.storedInBin"));
  };




  const placedCount = spareParts.filter((p) => !!p.bin_id).length;
  const storableParts = spareParts.filter((p) => p.extraction_status !== "damaged");
  // If every extracted part is damaged, there's nothing to bin — treat as placed.
  const allPlaced = storableParts.length === 0
    ? spareParts.length > 0
    : storableParts.every((p) => !!p.bin_id);
  // Skip the "labels printed" gate when there are no storable parts to label.
  const storableIds = storableParts.map((p) => p.id);
  const printedIds = hydratePrinted(
    { printedPartIds: printedPartIds ?? undefined, labelsPrinted: legacyLabelsPrinted },
    storableIds,
  );
  // All storable parts printed (individually or via "Print all labels").
  const labelsPrinted = allLabelsPrinted(storableIds, printedIds);
  const printedIdsRef = useRef(printedIds);
  printedIdsRef.current = printedIds;
  const labelsReady = storableParts.length === 0 ? true : labelsPrinted;

  // Auto-advance to Step 5 once all (non-damaged) parts have been placed in bins
  // and labels have been printed. Also flip the work order to "completed" so it
  // doesn't get stuck in_progress when the user navigates away before clicking
  // "Back to Work Orders". WorkflowRunner will then force-jump to the
  // workflow_complete module (harvested_parts summary).
  const [completionFinalized, setCompletionFinalized] = useState(false);
  useEffect(() => {
    if (step >= 3 && allPlaced && labelsReady && !completionFinalized) {
      setCompletionFinalized(true);
      (async () => {
        await persistSubStep({
          harvestStep: 5,
          sparePartIds,
          labelsPrinted,
          allPlaced: true,
        });
        setStep(5);
        const woId = runner?.workOrderId ?? null;
        if (woId) {
          const { error } = await (supabase as any)
            .from("work_orders")
            .update({ status: "completed", completed_at: new Date().toISOString() })
            .eq("id", woId);
          if (error) {
            console.error("[HarvestProcess] failed to mark work order completed", error);
          }
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPlaced, labelsReady, step, sparePartIds.join(",")]);

  // Re-arm completion auto-advance whenever the user steps away from step 4/5
  // (e.g. back-edit from Step 5 → Step 4).
  useEffect(() => {
    if (step !== 5) {
      setCompletionFinalized(false);
    }
  }, [step]);

  // ---- Step 2: Extract & Confirm helpers ----
  const setExtractionStatus = async (partId: string, status: "pending" | "extracted" | "damaged") => {
    const patch: Record<string, unknown> =
      status === "damaged"
        ? { extraction_status: "damaged", status: "damaged", bin_id: null }
        : { extraction_status: status, status: "pending_placement" };
    const { error } = await supabase
      .from("spare_parts")
      .update(patch)
      .eq("id", partId);
    if (error) { toast.error(error.message); return; }
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", sparePartIds.join(",")] });
    refetchParts();
  };

  const removePart = async (partId: string) => {
    if (labelsPrinted) return;
    const { error } = await supabase.from("spare_parts").delete().eq("id", partId);
    if (error) { toast.error(error.message); return; }
    const nextIds = sparePartIds.filter((sid) => sid !== partId);
    setSparePartIds(nextIds);
    await persistSubStep({
      harvestStep: 2,
      sparePartIds: nextIds,
      labelsPrinted,
    });
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", nextIds.join(",")] });
    refetchParts();
    toast.success(t("process.partRemoved"));
  };

  const uploadExtractionPhoto = async (partId: string, file: File) => {
    if (!user) return;
    setUploadingPhotoFor(partId);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${user.id}/${partId}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("spare-part-photos")
        .upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("spare-part-photos").getPublicUrl(path);
      const { error: updErr } = await supabase
        .from("spare_parts")
        .update({ extraction_photo_url: pub.publicUrl })
        .eq("id", partId);
      if (updErr) throw updErr;
      queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", sparePartIds.join(",")] });
      refetchParts();
      toast.success(t("process.photoAttached"));
    } catch (e) {
      const err = e as Error;
      toast.error(t("process.photoUploadFailed", { error: err.message }));
    } finally {
      setUploadingPhotoFor(null);
    }
  };

  const addHarvestedPart = async () => {
    if (!user || !addPartComponentId) return;
    const c = harvestableComponents.find((h) => h.id === addPartComponentId);
    if (!c) return;
    try {
      const { data: companyId, error: cErr } = await supabase
        .rpc("get_user_company_id", { _user_id: user.id });
      if (cErr) throw cErr;
      if (!companyId) throw new Error(t("process.noCompany"));

      const donor = sparePartDonorRow(await loadDonorAppliance(), runner?.workOrderId, { id: c.id, name: c.name, material: c.material });
      if ("missing" in donor) { reportDonorMissing(donor.missing); return; }

      const { data: inserted, error } = await supabase
        .from("spare_parts")
        .insert({
          ...donor.row,
          company_id: companyId,
          origin_company_id: companyId, // overwritten by trigger
          user_id: user.id,
          component_id: (c.id.startsWith("custom-") || isEnginePartId(c.id)) ? null : c.id,
          part_type_id: await resolvePartTypeId(
            c.name,
            applianceBrand || null,
            isEnginePartId(c.id) ? c.material : null,
          ),
          name: c.name,
          weight_kg: c.weight,
          estimated_value: c.value,
          notes: c.isHazardous ? "Hazardous material" : null,
          extraction_status: "extracted",
        })
        .select("id")
        .single();
      if (error && /appliance_company_mismatch|work_order_mismatch/.test(error.message)) { toast.error(t("donor.otherCompany")); return; }
      if (error) throw error;
      const nextIds = [...sparePartIds, inserted.id];
      setSparePartIds(nextIds);
      await persistSubStep({
        harvestStep: 2,
        sparePartIds: nextIds,
        labelsPrinted,
      });
      queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", nextIds.join(",")] });
      refetchParts();
      setShowAddPartDialog(false);
      setAddPartComponentId("");
    } catch (e) {
      const err = e as Error;
      toast.error(err.message);
    }
  };

  const allExtracted = spareParts.length > 0 && spareParts.every((p) =>
    p.extraction_status === "extracted" || p.extraction_status === "damaged"
  );

  const continueToLabelPrinting = async () => {
    setAdvancingToLabels(true);
    try {
      await persistSubStep({
        harvestStep: 3,
        sparePartIds,
        labelsPrinted,
      });
      setStep(3);
    } finally {
      setAdvancingToLabels(false);
    }
  };

  const goBackToExtraction = async () => {
    if (labelsPrinted) return;
    await persistSubStep({
      harvestStep: 2,
      sparePartIds,
      labelsPrinted: false,
    });
    setStep(2);
  };

  const { settings: harvestLabelPrintingSettings } = useHarvestLabelPrintingSettings();
  const {
    zebraPrint: harvestZebraPrint,
    isZebraAvailable: harvestZebraAvailable,
  } = useHarvestLabelPrinter();

  /** Records parts as printed and persists the list with the workflow state. */
  const markPrinted = async (ids: string[]) => {
    const next = addPrinted(printedIdsRef.current, ids);
    printedIdsRef.current = next;
    setPrintedPartIds(next);
    await persistSubStep({
      harvestStep: 3,
      sparePartIds,
      printedPartIds: next,
      labelsPrinted: allLabelsPrinted(storableIds, next),
    });
  };

  /**
   * Neither browser print (Print vs Cancel) nor QZ Tray (queue accepted ≠ label
   * came out) proves a label printed, so ask — but only about parts that are
   * not printed yet. Reprints of printed parts just print.
   */
  const askPrintConfirmation = (ids: string[], via: "browser" | "zebra") => {
    const pending = idsNeedingConfirmation(ids, printedIdsRef.current);
    if (pending.length === 0) return;
    setConfirmPrintVia(via);
    setConfirmPrintIds(pending);
  };

  const printLabelsNow = async () => {
    const ids = storableIds;
    setPrintOnlyId(null);
    await printAfterMenuCloses();
    askPrintConfirmation(ids, "browser");
  };

  const zebraPrintAllLabels = async () => {
    const size = resolveSparePartLabelSize(harvestLabelPrintingSettings.defaultLabelSize);
    const printable = spareParts.filter((p) => p.extraction_status !== "damaged");
    const jobs = buildSparePartPrintJobs(
      printable
        .map((p) => ({
          id: p.id,
          name: p.name,
          serial_no: p.serial_no,
          qr_token: p.qr_token,
          brand: p.brand,
          model_number: p.model_number,
          harvested_at: p.harvested_at,
        })),
      {
        size,
        locale: i18n.language || "en-GB",
        captions: {
          sourced: t("process.label.sourced").replace(/:$/, ""),
          harvested: t("process.label.harvested").replace(/:$/, ""),
        },
      },
    );
    // Throws on failure; the print menu shows the error toast and no dialog opens.
    await harvestZebraPrint(jobs);
    askPrintConfirmation(printable.map((p) => p.id), "zebra");
  };

  const printSingleLabel = async (partId: string) => {
    setPrintOnlyId(partId);
    try {
      await printAfterMenuCloses();
    } finally {
      setPrintOnlyId(null);
    }
    askPrintConfirmation([partId], "browser");
  };

  const zebraPrintSingleLabel = async (partId: string) => {
    const p = spareParts.find((x) => x.id === partId);
    if (!p) return;
    const size = resolveSparePartLabelSize(harvestLabelPrintingSettings.defaultLabelSize);
    const jobs = buildSparePartPrintJobs(
      [
        {
          id: p.id,
          name: p.name,
          serial_no: p.serial_no,
          qr_token: p.qr_token,
          brand: p.brand,
          model_number: p.model_number,
          harvested_at: p.harvested_at,
        },
      ],
      {
        size,
        locale: i18n.language || "en-GB",
        captions: {
          sourced: t("process.label.sourced").replace(/:$/, ""),
          harvested: t("process.label.harvested").replace(/:$/, ""),
        },
      },
    );
    await harvestZebraPrint(jobs);
    askPrintConfirmation([partId], "zebra");
  };


  const continueToStorage = async () => {
    await persistSubStep({
      harvestStep: 4,
      sparePartIds,
      labelsPrinted: true,
    });
    setStep(4);
  };

  const assignBinAndRemember = async (partId: string, binId: string) => {
    await assignBin(partId, binId);
    setLastScannedBinId(binId);
  };

  const assignSameBin = async (partId: string) => {
    if (!lastScannedBinId) return;
    await assignBin(partId, lastScannedBinId);
  };

  const togglePartSelection = (partId: string) => {
    setSelectedPartIds((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) next.delete(partId);
      else next.add(partId);
      return next;
    });
  };

  const clearSelection = () => setSelectedPartIds(new Set());

  const assignBinToMany = async (binId: string, partIds: string[]) => {
    if (partIds.length === 0) return;
    const { error } = await supabase
      .from("spare_parts")
      .update({ bin_id: binId, status: "stored" })
      .in("id", partIds);
    if (error) {
      toast.error(t("process.failedToAssign", { error: error.message }));
      return;
    }
    setLastScannedBinId(binId);
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", sparePartIds.join(",")] });
    queryClient.invalidateQueries({ queryKey: ["spare_parts"] });
    refetchParts();
    const binAddr = bins.find((b) => b.id === binId)?.address ?? "";
    toast.success(
      t("process.assignedToast", {
        count: partIds.length,
        bin: binAddr,
        defaultValue: "{{count}} part(s) stored in {{bin}}",
      }),
    );
    clearSelection();
  };

  const unassignBin = async (partId: string) => {
    const { error } = await supabase
      .from("spare_parts")
      .update({ bin_id: null, status: "pending_placement" })
      .eq("id", partId);
    if (error) {
      toast.error(error.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", sparePartIds.join(",")] });
    queryClient.invalidateQueries({ queryKey: ["spare_parts"] });
    refetchParts();
    toast.success(t("process.unassignedToast", { defaultValue: "Unassigned" }));
  };

  // Scanner: classify token as part (serial/qr_token) or bin (address/qr_token)
  const handleScan = (rawToken: string) => {
    // Extract trailing token from a URL like /spare-part/<token> or /bin/<id>
    const cleaned = rawToken.replace(/\s+/g, "");
    const lastSeg = cleaned.split("/").filter(Boolean).pop() || cleaned;
    const upper = lastSeg.toUpperCase();

    // Try part match first (against storable parts on this page)
    const matchedPart = storableParts.find(
      (p) =>
        (p.serial_no && p.serial_no.toUpperCase() === upper) ||
        (p.qr_token && p.qr_token.toUpperCase() === upper) ||
        (p.serial_no && cleaned.includes(p.serial_no)) ||
        (p.qr_token && cleaned.includes(p.qr_token)),
    );
    if (matchedPart) {
      togglePartSelection(matchedPart.id);
      return;
    }

    // Try bin match: by id on new labels, by code on old ones (FIX-319)
    const binScan = resolveScannedBin(rawToken, bins);
    if (binScan.kind !== "none") {
      const targets = Array.from(selectedPartIds);
      if (targets.length === 0) {
        toast.message(
          t("process.scanBinNoSelection", {
            defaultValue: "Scan or click a part first, then scan the bin.",
          }),
        );
        return;
      }
      if (binScan.kind === "bin") assignBinToMany(binScan.bin.id, targets);
      else setBinChoice({ bins: binScan.bins, onPick: (b) => assignBinToMany(b.id, targets) });
      return;
    }

    toast.error(
      t("process.scanUnmatched", {
        token: rawToken,
        defaultValue: "No matching part or bin for \"{{token}}\"",
      }),
    );
  };

  const handleScanBinClick = async (partId: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      setScannerStream(stream);
      setScanningPartId(partId);
    } catch (err) {
      const e = err as Error;
      toast.error(
        e.name === "NotAllowedError"
          ? t("bin.cameraDenied")
          : t("bin.cameraError", { message: e.message }),
      );
    }
  };

  // Active only on Step 4; pause when bin modal or camera scanner is open
  useScannerInput(handleScan, {
    disabled: step !== 4 || binModalOpen || !!scanningPartId || !!binChoice,
  });

  const completeWorkOrder = async () => {
    const woId = runner?.workOrderId ?? null;
    if (woId) {
      await (supabase as any)
        .from("work_orders")
        .update({ status: "completed", completed_at: new Date().toISOString() })
        .eq("id", woId);
    }
    navigate("/work-orders");
  };

  const partsNeedingBin = Math.max(storableParts.length - placedCount, 0);
  const recoveredValueEuros = resaleValue(Number(selectionSummary.totalValue), resalePctFraction).toFixed(2);



  const stepCopy: { title?: string; hint?: React.ReactNode } = (() => {
    switch (step) {
      case 1:
        // Title/hint moved into the step-1 card itself; recommendation source
        // panel is rendered right beneath the appliance header.
        return { title: undefined, hint: undefined };
      case 2:
        return { title: t("process.extractAndConfirm"), hint: t("process.extractAndConfirmHint") };
      case 3:
        return { title: t("process.printLabelsTitle"), hint: t("process.printLabelsHint") };
      case 4:
        return {
          title: t("process.storeBinsTitle"),
          hint: (
            <>
              {t("process.storeBinsHint")}{" "}
              <button
                type="button"
                onClick={() => setShowBinHelp(true)}
                className="text-primary hover:underline"
              >
                {t("process.binHelpLink")}
              </button>
            </>
          ),
        };
      case 5:
      default:
        return { title: t("process.summaryTitle"), hint: t("process.summaryInventoryNote") };
    }
  })();


  const renderComponentRow = (component: HarvestComponent, onToggle?: () => void) => (
                  <div
                    key={component.id}
                    className={`flex items-start gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${
                      selectedComponents.includes(component.id)
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    onClick={() => (onToggle ? onToggle() : toggleComponent(component.id))}
                  >
                    <Checkbox
                      checked={selectedComponents.includes(component.id)}
                      onCheckedChange={() => (onToggle ? onToggle() : toggleComponent(component.id))}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground">{component.name}</span>
                        {component.id.startsWith("custom-") && (
                          <span className="px-1.5 py-0.5 text-xs bg-accent text-accent-foreground rounded">{t("process.custom")}</span>
                        )}
                      </div>
                      {component.description && (
                        <p className="text-sm text-muted-foreground mb-2">{component.description}</p>
                      )}
                      {component.isHazardous && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 bg-warning/20 text-warning text-xs font-medium rounded">{t("process.hazardous")}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 flex items-start gap-2">
                      <div>
                        
                        <p className="text-[10px] text-muted-foreground leading-tight">Catalogue Net Trade Price</p>
                        <p className="text-sm text-harvest font-medium">€{component.value.toFixed(2)}</p>
                        <p className="text-[10px] text-muted-foreground leading-tight">≈ {component.co2eKg.toFixed(1)} kg CO₂e</p>
                      </div>
                      {component.id.startsWith("custom-") && (
                        <Button
                          variant="ghost" size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); removeCustomItem(component.id); }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <HarvestWorkflowHeader
        applianceId={id}
        workOrderId={runner?.workOrderId ?? null}
        brand={applianceBrand}
        model={storedAppliance?.modelNumber}
        serial={loadedAppliance?.serialNumber}
        category={storedAppliance?.category}
        currentStep={step}
        stepTitle={stepCopy.title}
        stepHint={stepCopy.hint}
        headerActions={
          step === 3 ? (
            <SparePartLabelPrintMenu
              namespace="harvest"
              mode="all"
              className="w-full sm:w-auto"
              onPrintBrowser={printLabelsNow}
              onPrintZebra={zebraPrintAllLabels}
              isZebraAvailable={harvestZebraAvailable}
            />
          ) : step === 4 ? (
            <p className="text-sm text-muted-foreground">
              {t("process.partsPlaced", { placed: placedCount, total: storableParts.length })}
            </p>
          ) : undefined
        }
        applianceTrailing={
          step === 2 ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Captured</p>
              <p className="mt-1 text-base font-semibold text-harvest">
                €{(spareParts
                  .filter((p) => p.extraction_status === "extracted")
                  .reduce((sum, p) => sum + (Number(p.estimated_value) || 0), 0) * resalePctFraction
                ).toFixed(0)}
              </p>
            </div>
          ) : undefined
        }
      />

      <main className="max-w-6xl mx-auto px-6 py-6 pb-28 sm:pb-24 print:pb-0">
        {/* Recommendation source — shown on the selection step, paired with the appliance header above */}
        {step === 1 && recommendations && (
          <div className="mb-6 rounded-2xl border border-border bg-card shadow-sm p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Leaf className="w-3.5 h-3.5 text-green-600" />
                  Recommendation source
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  FixID {recommendations.sheetNotes?.trim() || recommendations.sheetFilename || new Date(recommendations.sheetVersion).toLocaleDateString()}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Matched on {[recommendations.matchedOn.category, recommendations.matchedOn.technology, recommendations.matchedOn.brand].filter(Boolean).join(" / ") || "—"}
                </div>
              </div>
              <Link
                to="/settings/parts-harvesting"
                className="text-[11px] text-primary hover:underline whitespace-nowrap"
              >
                Manage →
              </Link>
            </div>
            <div className="mt-3 pt-3 border-t border-border/60">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center justify-between gap-2">
                <span>Thresholds from Parts Harvesting</span>
                <Link to="/settings/parts-harvesting" className="text-primary normal-case tracking-normal hover:underline">
                  Edit
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded-md bg-background border border-border px-2 py-1.5">
                  <div className="text-[10px] text-muted-foreground">Min demand</div>
                  <div className="font-medium text-foreground capitalize">{recommendations.thresholds.min_demand}</div>
                </div>
                <div className="rounded-md bg-background border border-border px-2 py-1.5">
                  <div className="text-[10px] text-muted-foreground">Min priority</div>
                  <div className="font-medium text-foreground">{recommendations.thresholds.min_priority}</div>
                </div>
                <div className="rounded-md bg-background border border-border px-2 py-1.5">
                  <div className="text-[10px] text-muted-foreground">Min price</div>
                  <div className="font-medium text-foreground">€{recommendations.thresholds.min_price_eur}</div>
                </div>
                <div className="rounded-md bg-background border border-border px-2 py-1.5">
                  <div className="text-[10px] text-muted-foreground">Resale %</div>
                  <div className="font-medium text-foreground">{recommendations.thresholds.resale_price_pct <= 1 ? Math.round(recommendations.thresholds.resale_price_pct * 100) : recommendations.thresholds.resale_price_pct}%</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Matching manuals */}
        <div className="mb-6 print:hidden space-y-4">

          <MatchingManualsPanel
            brand={applianceBrand || "BOSCH"}
            modelNumber={storedAppliance?.modelNumber}
            activeManualId={manualId}
            onSelectForAI={(mid, title, kind) => {
              setManualId(mid);
              setManualTitle(title);
              setManualIsFallback(kind === "fallback");
              toast.success(t("process.manualAttached", { title }));
            }}
          />
          <ExplodedViewsPanel
            brand={applianceBrand || "BOSCH"}
            applianceType={storedAppliance?.category}
            modelNumber={storedAppliance?.modelNumber}
          />
        </div>





        {/* STEP 1: Select Materials */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-foreground">
                  {t("process.harvestableComponents")}
                </h2>
                <div className="flex items-center gap-3">
                  {(() => {
                    const visible = harvestableComponents.filter((c) => !uncertainIds.has(c.id));
                    const allVisibleSelected = visible.length > 0 && visible.every((c) => selectedComponents.includes(c.id));
                    return (
                      <>
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={(checked) => {
                            applySelection(checked
                              ? [...new Set([...selectedComponents, ...visible.map((c) => c.id)])]
                              : selectedComponents.filter((sid) => !visible.some((c) => c.id === sid)));
                          }}
                          className="w-5 h-5"
                        />
                        <span className="text-sm text-muted-foreground">
                          {selectedComponents.length === 0
                            ? t("process.selectAll")
                            : t("process.selectedCount", { selected: selectedComponents.length, total: harvestableComponents.length })}
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>

              {categoryMissing && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-3 mb-3 text-sm flex items-center justify-between gap-3">
                  <span>{t("completeAppliance.categoryNeeded")}</span>
                  <Button size="sm" variant="outline" onClick={() => setCompleteOpen(true)}>{t("completeAppliance.open")}</Button>
                </div>
              )}
              {!categoryMissing && !recommendations && !recsLoading && !recsError && (
                <div className="text-xs text-muted-foreground -mt-1 mb-3">
                  Using legacy component list — no active recommendation sheet uploaded.
                </div>
              )}




              <div className="space-y-3">
                {harvestableComponents.length === 0 && (
                  <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground text-sm">
                    {recsExpected && recsLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading recommendations…
                      </>
                    ) : recommendations && recommendations.items.length === 0 ? (
                      "No commercial pricing available for this brand/category yet."
                    ) : (
                      t("process.noComponents")
                    )}
                  </div>
                )}

                {techGrouping.common.map((component) => renderComponentRow(component))}
                {techGrouping.unclear && (
                  <div className="mt-4 rounded-xl border border-warning/40 bg-warning/10 p-4 space-y-3" role="status">
                    <div className="flex items-start gap-2 text-sm text-foreground">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
                      <span>{t("process.technologyUnclear")}</span>
                    </div>
                    {mergeUncertainByName(techGrouping.uncertain).map((row) =>
                      row.items.length === 1
                        ? renderComponentRow(row.items[0])
                        : renderComponentRow(
                            {
                              ...row.items[0],
                              id: row.items[0].id,
                              name: `${row.name} — ${row.technologies.map((tech) => t(`process.technologyNames.${tech}`)).join(t("process.technologyOr"))}`,
                            },
                            () => setPendingTechs(row.technologies),
                          ),
                    )}
                  </div>
                )}
                <AlertDialog open={!!pendingTechs} onOpenChange={(o) => { if (!o && !savingTech) setPendingTechs(null); }}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {pendingTechs && (pendingTechs.length === 1
                          ? t(`process.confirmTechnology.${dryingKind === "washer_dryer" ? "washerDryer" : "dryer"}.${pendingTechs[0]}`)
                          : t("process.confirmTechnology.choice", {
                              options: pendingTechs
                                .map((tech) => t(`process.confirmTechnology.options.${dryingKind === "washer_dryer" ? "washerDryer" : "dryer"}.${tech}`))
                                .join(t("process.technologyOr")),
                            }))}
                      </AlertDialogTitle>
                      <AlertDialogDescription>{t("process.confirmTechnology.description")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={savingTech}>{t("process.confirmTechnology.cancel")}</AlertDialogCancel>
                      {pendingTechs?.length === 1 ? (
                        <AlertDialogAction disabled={savingTech} onClick={(e) => { e.preventDefault(); void confirmTechnology(pendingTechs[0]); }}>
                          {t("process.confirmTechnology.confirm")}
                        </AlertDialogAction>
                      ) : (
                        pendingTechs?.map((tech) => (
                          <AlertDialogAction key={tech} disabled={savingTech} onClick={(e) => { e.preventDefault(); void confirmTechnology(tech); }}>
                            {t(`process.confirmTechnology.buttons.${tech}`)}
                          </AlertDialogAction>
                        ))
                      )}
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Add custom */}
              {showAddForm ? (
                <div className="mt-4 p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground text-sm">{t("process.addCustomComponent")}</h4>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowAddForm(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder={t("process.componentNamePlaceholder")} value={newItemName} onChange={(e) => setNewItemName(e.target.value)} className="col-span-2" />
                    <Input placeholder={t("process.weightPlaceholder")} type="number" step="0.1" value={newItemWeight} onChange={(e) => setNewItemWeight(e.target.value)} />
                    <Input placeholder={t("process.valuePlaceholder")} type="number" step="0.01" value={newItemValue} onChange={(e) => setNewItemValue(e.target.value)} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addCustomItem} disabled={!newItemName.trim()} className="gap-1 bg-harvest hover:bg-harvest/90">
                      <Plus className="w-3.5 h-3.5" /> {t("process.addComponent")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>{t("process.cancel")}</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="mt-4 w-full gap-2 border-dashed" onClick={() => setShowAddForm(true)}>
                  <Plus className="w-4 h-4" /> {t("process.addCustomComponent")}
                </Button>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-semibold text-foreground mb-4">{t("process.selectionSummary")}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("process.components")}</span><span className="font-medium">{t("process.componentsSelected", { count: selectionSummary.count })}</span></div>
                  
                  <div className="flex justify-between"><span className="text-muted-foreground">Total Recovery Value</span><span className="font-medium text-harvest">{selectionSummary.totalRecoveryLabel}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("process.recoveredValue")}</span><span className="font-medium text-harvest">€{recoveredValueEuros}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("process.avgRecovery")}</span><span className="font-medium">{selectionSummary.avgRecovery}%</span></div>
                </div>
              </div>


              {/* Env impact */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Leaf className="w-5 h-5 text-harvest" />
                  <h3 className="font-semibold text-foreground">{t("process.environmentalImpact")}</h3>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-xs">{t("process.co2eTooltip")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-harvest/10 rounded-xl p-3">
                    <p className="text-xl font-bold text-harvest">{selectionSummary.totalWeight}kg</p>
                    <p className="text-xs text-muted-foreground">{t("process.recovered")}</p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-3">
                    <p className="text-xl font-bold text-foreground">{selectionSummary.co2}kg</p>
                    <p className="text-xs text-muted-foreground">{t("process.co2Saved")}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-semibold text-foreground mb-4">{t("process.safetyWarnings")}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{t("process.hazardousMaterials")}</p>
                      <p className="text-sm text-muted-foreground">{t("process.hazardousMaterialsDesc")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{t("process.sharpEdges")}</p>
                      <p className="text-sm text-muted-foreground">{t("process.sharpEdgesDesc")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Extract & Confirm */}
        {step === 2 && (
          <div className="space-y-6">


            <div className="space-y-3">
              {spareParts.length === 0 && (
                <div className="text-center text-muted-foreground py-8 bg-card rounded-2xl border border-border">
                  {t("process.loadingParts")}
                </div>
              )}
              {spareParts.map((p, idx) => {
                const status = (p.extraction_status || "pending") as "pending" | "extracted" | "damaged";
                const statusPill =
                  status === "extracted" ? "bg-harvest/10 text-harvest" :
                  status === "damaged" ? "bg-destructive/10 text-destructive" :
                  "bg-muted text-muted-foreground";
                const indexTone =
                  status === "extracted" ? "bg-harvest/15 text-harvest" :
                  status === "damaged" ? "bg-destructive/15 text-destructive" :
                  "bg-muted text-muted-foreground";
                const statusLabel = t(`process.${status}`);
                const subParts = [
                  p.part_number || null,
                  p.estimated_value != null ? `€${Number(p.estimated_value).toFixed(0)}` : null,
                ].filter(Boolean) as string[];
                return (
                  <div
                    key={p.id}
                    className="bg-card rounded-2xl border border-border p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold shrink-0 ${indexTone}`}>
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-foreground truncate">{p.name}</p>
                        {subParts.length > 0 && (
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {subParts.join(" · ")}
                          </p>
                        )}
                        {p.extraction_photo_url && (
                          <a href={p.extraction_photo_url} target="_blank" rel="noreferrer" className="inline-block mt-2">
                            <img
                              src={p.extraction_photo_url}
                              alt={p.name}
                              className="h-16 w-16 rounded-md object-cover border border-border"
                            />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-end sm:flex-shrink-0">
                      {status !== "pending" && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusPill}`}>
                          {statusLabel}
                        </span>
                      )}

                      {status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-harvest hover:bg-harvest/90"
                            onClick={() => setExtractionStatus(p.id, "extracted")}
                          >
                            {t("process.markExtracted")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setExtractionStatus(p.id, "damaged")}
                          >
                            {t("process.damaged")}
                          </Button>
                        </>
                      )}
                      {status !== "damaged" && (
                        <label className="inline-flex">
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadExtractionPhoto(p.id, f);
                              e.currentTarget.value = "";
                            }}
                          />
                          <Button
                            asChild
                            size="icon"
                            variant="outline"
                            disabled={uploadingPhotoFor === p.id}
                            title={t("process.attachPhoto")}
                          >
                            <span><Camera className="w-4 h-4" /></span>
                          </Button>
                        </label>
                      )}
                      {status !== "pending" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="font-normal text-muted-foreground"
                          onClick={() => setExtractionStatus(p.id, "pending")}
                        >
                          {t("process.undo")}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}


              <Button
                variant="outline"
                className="w-full gap-2 border-dashed"
                onClick={() => setShowAddPartDialog(true)}
              >
                <Plus className="w-4 h-4" /> {t("process.addHarvestedPart")}
              </Button>
            </div>
          </div>
        )}

        <Dialog open={showAddPartDialog} onOpenChange={setShowAddPartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("process.addPartDialogTitle")}</DialogTitle>
              <DialogDescription>{t("process.addPartDialogHint")}</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <SearchableSelect
                value={addPartComponentId || null}
                onChange={(v) => setAddPartComponentId(v ?? "")}
                placeholder={t("process.pickComponents")}
                searchPlaceholder={t("process.searchComponents")}
                emptyMessage={t("process.noComponents")}
                items={addPartItems}
              />

            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddPartDialog(false)}>{t("process.cancel")}</Button>
              <Button
                className="bg-harvest hover:bg-harvest/90"
                disabled={!addPartComponentId}
                onClick={addHarvestedPart}
              >
                {t("process.confirmAdd")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* STEP 3: Print Labels */}
        {step === 3 && (
          <div className="space-y-6">
            {labelsPrinted && (
              <p className="text-xs text-muted-foreground print:hidden">{t("process.labelsLocked")}</p>
            )}

            <div className="space-y-4 print:p-0">
              {storableParts.length === 0 && (
                <div className="text-center text-muted-foreground py-8">{t("process.loadingParts")}</div>
              )}
              {storableParts.map((p, idx) => {
                const qrUrl = `${window.location.origin}/spare-part/${p.serial_no || p.qr_token}`;
                const isPrinted = printedIds.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className={`bg-card rounded-xl border border-border p-4 flex flex-col md:flex-row md:items-start gap-4 print:break-inside-avoid print:border-0 print:bg-transparent print:p-0 print:rounded-none print:shadow-none`}
                  >
                    <div className="flex items-start gap-3 md:gap-4 md:contents">
                      <div className="w-9 h-9 rounded-xl bg-harvest text-harvest-foreground flex items-center justify-center font-bold flex-shrink-0 md:self-start print:hidden">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0 print:hidden">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">{p.name}</h3>
                          {isPrinted && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-harvest/10 text-harvest px-2 py-0.5 text-xs font-medium">
                              <CheckCircle2 className="w-3 h-3" /> {t("process.labelPrintedBadge")}
                            </span>
                          )}
                        </div>
                        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <dt>{t("process.labelMeta.category")}</dt>
                          <dd className="text-foreground">{p.parts_category_name || "—"}</dd>
                          <dt>{t("process.labelMeta.partId")}</dt>
                          <dd className="text-foreground font-mono">{p.serial_no || p.qr_token.slice(0, 12).toUpperCase()}</dd>
                          <dt>{t("process.labelMeta.sourced")}</dt>
                          <dd className="text-foreground">{[p.brand, p.model_number].filter(Boolean).join(" ") || "—"}</dd>
                          <dt>{t("process.labelMeta.harvested")}</dt>
                          <dd className="text-foreground">{formatLabelDate(p.harvested_at, i18n.language || "en-GB")}</dd>
                        </dl>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 md:flex-shrink-0 w-full md:w-[420px]">
                      <div className="rounded-xl p-2 print:p-0">
                        <SparePartLabel
                          qrValue={qrUrl}
                          partName={p.name}
                          partId={p.serial_no || p.qr_token.slice(0, 12).toUpperCase()}
                          sourceBrand={p.brand}
                          sourceModel={p.model_number}
                          harvestedAt={p.harvested_at}
                          qrSize={140}
                        />
                      </div>
                      <SparePartLabelPrintMenu
                        namespace="harvest"
                        mode="single"
                        variant="outline"
                        size="sm"
                        className="w-full print:hidden"
                        onPrintBrowser={() => printSingleLabel(p.id)}
                        onPrintZebra={() => zebraPrintSingleLabel(p.id)}
                        isZebraAvailable={harvestZebraAvailable}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Print-only copy of the label(s) under <body>: exactly one page per label. */}
        {step === 3 && storableParts.length > 0 && (
          <HarvestLabelPrintRoot>
            {storableParts
              .filter((p) => printOnlyId === null || p.id === printOnlyId)
              .map((p) => (
                <HarvestPrintLabelPage key={p.id}>
                  <SparePartLabel
                    qrValue={`${window.location.origin}/spare-part/${p.serial_no || p.qr_token}`}
                    partName={p.name}
                    partId={p.serial_no || p.qr_token.slice(0, 12).toUpperCase()}
                    sourceBrand={p.brand}
                    sourceModel={p.model_number}
                    harvestedAt={p.harvested_at}
                    qrSize={140}
                  />
                </HarvestPrintLabelPage>
              ))}
          </HarvestLabelPrintRoot>
        )}

        {/* Neither browser nor Zebra prints prove a label came out — only "Yes" counts. */}
        <AlertDialog open={confirmPrintIds !== null} onOpenChange={(o) => { if (!o) setConfirmPrintIds(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("process.printConfirm.title", { count: confirmPrintIds?.length ?? 1 })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(confirmPrintVia === "zebra" ? "process.printConfirm.zebraDescription" : "process.printConfirm.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmPrintIds(null)}>
                {t("process.printConfirm.no")}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-harvest hover:bg-harvest/90"
                onClick={() => {
                  const ids = confirmPrintIds;
                  setConfirmPrintIds(null);
                  if (ids?.length) void markPrinted(ids);
                }}
              >
                {t("process.printConfirm.yes")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* STEP 4: Store in Bins */}
        {step === 4 && (
          <div className="space-y-6 pb-24">
            <div className="space-y-3">
              {storableParts.length === 0 && (
                <div className="text-center text-muted-foreground py-8">{t("process.loadingParts")}</div>
              )}
              {storableParts.map((p, idx) => {
                const qrUrl = `${window.location.origin}/spare-part/${p.serial_no || p.qr_token}`;
                const placedBin = bins.find((b) => b.id === p.bin_id);
                const isSelected = selectedPartIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    onClick={(e) => {
                      // Ignore clicks on action buttons / interactive children
                      const target = e.target as HTMLElement;
                      if (target.closest("button, a, [role='button']")) return;
                      togglePartSelection(p.id);
                    }}
                    className={`relative cursor-pointer bg-card rounded-xl border p-4 flex flex-col lg:flex-row gap-4 items-start transition-all ${
                      isSelected
                        ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                        : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div className="absolute top-3 left-3 z-10">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => togglePartSelection(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Select part"
                      />
                    </div>
                    <div className="flex-shrink min-w-0 max-w-full flex items-center gap-3 pl-8">
                      <SparePartLabel
                        qrValue={qrUrl}
                        partName={p.name}
                        partId={p.serial_no || p.qr_token.slice(0, 12).toUpperCase()}
                        sourceBrand={p.brand}
                        sourceModel={p.model_number}
                        harvestedAt={p.harvested_at}
                        qrSize={120}
                      />
                    </div>
                    <div className="flex-1 min-w-0 w-full pl-8 lg:pl-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-mono text-muted-foreground tabular-nums">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-semibold text-foreground truncate">{p.name}</h3>
                      </div>
                      <div className="mb-3">
                        {placedBin ? (
                          <span className="inline-flex items-center rounded-full bg-harvest/10 border border-harvest/30 text-harvest px-2.5 py-0.5 text-xs">
                            {t("process.stored")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-muted/70 border border-border/60 text-muted-foreground px-2.5 py-0.5 text-xs">
                            {t("process.awaitingPlacement")}
                          </span>
                        )}
                      </div>
                      {placedBin ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setSelectedPartIds(new Set([p.id])); setBinModalOpen(true); }}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 text-primary font-mono text-sm whitespace-nowrap hover:bg-primary/20 transition-colors cursor-pointer"
                              >
                                <Package className="w-3.5 h-3.5 text-primary/80" />
                                {placedBin.address}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{t("process.changeBin", { defaultValue: "Change bin" })}</TooltipContent>
                          </Tooltip>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => unassignBin(p.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            {t("process.unassignBin", { defaultValue: "Unassign bin" })}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            variant="default" size="sm"
                            onClick={() => { setSelectedPartIds(new Set([p.id])); setBinModalOpen(true); }}
                            className="gap-2"
                          >
                            <Package className="w-4 h-4" /> {t("process.selectBin", { defaultValue: "Select bin" })}
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => handleScanBinClick(p.id)}
                            className="gap-2"
                          >
                            <ScanLine className="w-4 h-4" /> {t("process.scanBin")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <BinScanner
              open={!!scanningPartId}
              initialStream={scannerStream}
              autoStart={false}
              onOpenChange={(o) => { if (!o) { setScanningPartId(null); setScannerStream(null); } }}
              onDetected={(text) => {
                if (scanningPartId) {
                  const partId = scanningPartId;
                  const binScan = resolveScannedBin(text, bins);
                  if (binScan.kind === "bin") assignBinToMany(binScan.bin.id, [partId]);
                  else if (binScan.kind === "choose") setBinChoice({ bins: binScan.bins, onPick: (b) => assignBinToMany(b.id, [partId]) });
                  else toast.error(t("process.scannedNoMatch", { address: text.trim() }));
                }
                setScanningPartId(null);
                setScannerStream(null);
              }}
            />

            <SelectionActionBar
              count={selectedPartIds.size}
              onSelectBin={() => setBinModalOpen(true)}
              onClear={clearSelection}
            />

            <BinAssignModal
              open={binModalOpen}
              onOpenChange={setBinModalOpen}
              selectedPartNames={Array.from(selectedPartIds)
                .map((id) => storableParts.find((p) => p.id === id)?.name || "")
                .filter(Boolean)}
              onAssign={(binId) => assignBinToMany(binId, Array.from(selectedPartIds))}
            />

            <BinHelpModal open={showBinHelp} onOpenChange={setShowBinHelp} />

            <BinLocationChoiceDialog choice={binChoice} onClose={() => setBinChoice(null)} />
          </div>
        )}


        {/* STEP 5: Summary */}
        {step === 5 && (
          <div className="space-y-6">

            {(() => {
              const harvestedParts = spareParts.filter((p) => p.extraction_status !== "damaged");
              const damagedParts = spareParts.filter((p) => p.extraction_status === "damaged");
              const shortId = (p: typeof spareParts[number]) =>
                (p.serial_no || p.qr_token || "");

              return (
                <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                  <h4 className="font-semibold text-foreground mb-4">
                    {t("process.summaryHarvestedPartsCount", { count: harvestedParts.length })}
                  </h4>

                  <div className="-mx-4 sm:mx-0 overflow-x-auto">
                    <div className="min-w-[28rem] px-4 sm:px-0">
                      {/* Column headers */}
                      <div className="grid grid-cols-[2rem_minmax(8rem,1fr)_auto_auto] gap-3 items-center px-1 pb-2 border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        <span>#</span>
                        <span>{t("process.col.part", "Part")}</span>
                        <span className="text-left">{t("process.col.bin", "Bin")}</span>
                        <span className="text-right">{t("process.col.value", "Value")}</span>
                      </div>

                      <div className="divide-y divide-border">
                        {harvestedParts.map((p, idx) => {
                          const placedBin = bins.find((b) => b.id === p.bin_id);
                          const rowContent = (
                            <div className="grid grid-cols-[2rem_minmax(8rem,1fr)_auto_auto] gap-3 items-center py-3 px-1">
                              <div className="w-7 h-7 rounded-md bg-harvest/15 text-harvest flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-foreground text-sm truncate">{p.name}</p>
                                {shortId(p) && (
                                  <p className="text-xs text-muted-foreground font-mono truncate">{shortId(p)}</p>
                                )}
                              </div>
                              <div>
                                {placedBin ? (
                                  <Link
                                    to={`/bin/${placedBin.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-block px-2.5 py-1 rounded-md bg-primary/10 text-primary font-mono text-xs whitespace-nowrap hover:bg-primary/20 transition-colors"
                                  >
                                    {placedBin.address}
                                  </Link>
                                ) : (
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {t("process.awaitingPlacement")}
                                  </span>
                                )}
                              </div>
                              <div className="text-right text-sm font-semibold text-harvest whitespace-nowrap min-w-[3rem]">
                                {p.estimated_value ? `€${Number(p.estimated_value).toFixed(0)}` : "—"}
                              </div>
                            </div>
                          );
                          return p.bin_id ? (
                            <Link
                              key={p.id}
                              to={`/spare-part/${p.serial_no || p.qr_token}`}
                              className="block hover:bg-muted/40 transition-colors rounded-md"
                            >
                              {rowContent}
                            </Link>
                          ) : (
                            <div key={p.id}>{rowContent}</div>
                          );
                        })}
                      </div>
                    </div>
                  </div>


                  {damagedParts.length > 0 && (
                    <details className="group mt-3 border-t border-border pt-3">
                      <summary className="flex items-center gap-2 cursor-pointer list-none text-sm text-muted-foreground hover:text-foreground">
                        <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180 shrink-0" />
                        <span>{t("process.damagedCount", { count: damagedParts.length })}</span>
                      </summary>
                      <div className="mt-2 divide-y divide-border">
                        {damagedParts.map((p, idx) => (
                          <div
                            key={p.id}
                            className="grid grid-cols-[2rem_1fr] sm:grid-cols-[2.5rem_1fr] gap-3 items-center py-3 px-1"
                          >
                            <div className="w-7 h-7 rounded-md bg-destructive/15 text-destructive flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-foreground text-sm truncate">{p.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {shortId(p) && <span className="font-mono">{shortId(p)}</span>}
                                {p.notes ? (
                                  <>
                                    {shortId(p) ? " · " : ""}
                                    <span>{t("process.col.reason", "Reason")}: {p.notes}</span>
                                  </>
                                ) : (
                                  <>
                                    {shortId(p) ? " · " : ""}
                                    <span className="text-destructive">{t("process.damaged")}</span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("process.summaryTotalValue")}</span>
                    <span className="font-semibold text-harvest">
                      €{(
                        spareParts
                          .filter((p) => p.extraction_status !== "damaged" && !!p.bin_id)
                          .reduce((s, p) => s + (Number(p.estimated_value) || 0), 0)
                        * resalePctFraction
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })()}

          </div>
        )}


        <AIAssistantDialog
          open={showAIAssistant}
          onOpenChange={setShowAIAssistant}
          currentStep={{
            phase: step,
            title:
              step === 1 ? t("process.stepSelectMaterials") :
              step === 2 ? t("process.stepExtractConfirm") :
              step === 3 ? t("process.stepPrintLabels") :
              step === 4 ? t("process.stepStoreBins") :
              t("process.stepComplete"),
          }}
          modelNumber={storedAppliance?.modelNumber || ""}
          manualId={manualId}
          manualTitle={manualTitle}
          manualIsFallback={manualIsFallback}
          manualSeriesLabel={seriesLabelFor(storedAppliance?.modelNumber)}
          applianceBrand={applianceBrand || null}
          onManualUploaded={(mid, title) => {
            setManualId(mid);
            setManualTitle(title);
            setManualIsFallback(false);
          }}
        />
      </main>

      {/* Sticky bottom action bar — mirrors /appliance/:id */}
      <div
        className="fixed bottom-0 right-0 left-0 z-30 bg-card border-t border-border print:hidden transition-[left] duration-200 ease-linear"
        style={{
          left: ctaLeftOffset,
          boxShadow: "0 -4px 16px -8px hsl(var(--foreground) / 0.08)",
        }}
      >

        <div
          className={`max-w-6xl mx-auto px-6 py-3 flex flex-col sm:flex-row gap-3 ${
            step === 5 ? "sm:items-center sm:justify-center" : "sm:items-center sm:justify-between"
          }`}
        >
          {step === 1 && (
            <>
              <div className="text-sm">
                <span className="text-muted-foreground">Selected Recovery: </span>
                <span className="font-bold text-harvest">€{recoveredValueEuros}</span>
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={selectionSummary.count === 0 ? 0 : -1} className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        className="w-full sm:w-auto gap-2 bg-harvest hover:bg-harvest/90"
                        disabled={processing || (!!runner && selectionSummary.count === 0)}
                        onClick={runner ? continueToLabels : () => navigate(`/appliance/${effectiveApplianceId}/workflow/harvesting`)}
                      >
                        {processing
                          ? t("process.processing")
                          : runner
                            ? (<>{t("process.continueToExtraction")} <ArrowRight className="w-4 h-4" /></>)
                            : (<>{t("process.startHarvestWorkOrder")} <ArrowRight className="w-4 h-4" /></>)}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {selectionSummary.count === 0 && (
                    <TooltipContent>{t("process.selectAtLeastOnePart")}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-sm text-muted-foreground">
                {t("process.confirmEachPart")}
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={!allExtracted ? 0 : -1} className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        className="w-full sm:w-auto gap-2 bg-harvest hover:bg-harvest/90"
                        disabled={!allExtracted || advancingToLabels}
                        onClick={continueToLabelPrinting}
                      >
                        {t("process.continueToPrintLabels")} <ChevronRight className="w-4 h-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!allExtracted && (
                    <TooltipContent>{t("process.markAllToContinue")}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-sm text-muted-foreground">
                {t("process.attachLabelsThenContinue")}
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={!labelsPrinted ? 0 : -1} className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        onClick={continueToStorage}
                        disabled={!labelsPrinted}
                        className="w-full sm:w-auto gap-2 bg-harvest hover:bg-harvest/90"
                      >
                        <CheckCircle2 className="w-4 h-4" /> {t("process.iveLabeledAll")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!labelsPrinted && (
                    <TooltipContent>{t("process.printFirstToContinue")}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {step === 4 && (
            <>
              <div className="text-sm text-muted-foreground">
                {partsNeedingBin > 0
                  ? t("process.partsNeedBin", { count: partsNeedingBin })
                  : t("process.allPartsPlaced")}
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={!allPlaced ? 0 : -1} className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        className="w-full sm:w-auto gap-2 bg-harvest hover:bg-harvest/90"
                        disabled={!allPlaced}
                        onClick={completeWorkOrder}
                      >
                        <CheckCircle2 className="w-4 h-4" /> {t("process.completeWorkOrder")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!allPlaced && (
                    <TooltipContent>{t("process.assignAllBinsTooltip")}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </>
          )}

          {step === 5 && (
            <Button
              size="lg"
              onClick={completeWorkOrder}
              className="w-full sm:w-auto gap-2 bg-harvest hover:bg-harvest/90"
            >
              <ArrowLeft className="w-4 h-4" /> {t("process.backToWorkOrders")}
            </Button>
          )}
        </div>
      </div>



      <style>{`
        @media print {
          @page { margin: 0; }
          body { background: white; }
        }
      `}</style>
      <CompleteApplianceDialog
        open={completeOpen}
        appliance={donorAppliance ?? null}
        companyId={currentCompany.id}
        reviewBrand={brandReview}
        onSaved={onApplianceCompleted}
        onCancel={() => setCompleteOpen(false)}
      />
    </div>
  );
}
